import { call, cancelled, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { placementMarginAssignmentActions } from './placement-assignment.redux';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import i18next from 'i18next';
import { MarginToolService } from 'app/services/MarginToolServices/margin-tool-service';
import { httpSuccess } from 'app/services/serviceHelpers';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  IPeopleSoftCalculationResult,
  IPeopleSoftMarginDetailsRequest,
} from '@AMIEWEB/MarginTool/Components/PeopleSoftCalculation/IPeopleSoftCalculation';
import {
  selectPlacementMarginCancelTriggeredStatus,
  selectPlacementMarginOverTimeHourlyRates,
  selectPlacementMarginToolDetailsData,
  selectPlacementMarginSaveTriggeredStatus,
  selectSelectedPlacementMarginScenario,
  selectPlacementMarginBookingPeriod,
} from '../placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { createRequestPayloadForPeopleSoftCalculation } from '@AMIEWEB/MarginTool/Components/PeopleSoftCalculation/IPeopleSoftCalculationHelper';
import { IAssignmentForm } from '@AMIEWEB/MarginTool/Components/Assignments/models/IAssignmentSplitForm';
import { selectUser } from 'oidc/user.selectors';
import {
  findMarginDetailByBookingPeriodId,
  getMarginOverTimeHourlyRatesFromScenario,
  updateMarginDetailsResponseByScenario,
  updateMarginOverTimeHourlyRatesFromCalculation,
} from '@AMIEWEB/MarginTool/Components/PayPackage/AddScenario/helper';
import { placementMarginScenarioActions } from '../placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { manuallyIncrementPromiseCounter, manuallyResetPromiseCounter, trackPromise } from 'react-promise-tracker';
import {
  IMarginUpdateResponse,
  IMarginUpdateResponseErrorTargetTypes,
  IMarginUpdateResponseErrorTypes,
} from '@AMIEWEB/MarginTool/Components/PayPackage/models/AddScenarioModal';
import { IMarginToolSave } from '@AMIEWEB/MarginTool/IMarginToolSave';
import { IBannerAction } from 'app/models/Global/Global';
import { selectPlacementMarginEditTimeOffs } from './placement-assignment.selector';
import { getFormattedTimeOff } from '@AMIEWEB/MarginTool/Components/Assignments/RequestedTimeOff/EditCellRender/helper';
import { IPlacementMarginOverTimeHourlyRateItem } from '../placement-add-edit-scenario/add-edit-placement-scenario.model';
import axios from 'axios';
import { getTlaShiftsPerWeek } from '@AMIEWEB/MarginTool/helper';
import { insuranceData, formatInsuranceTypeAheadOptions } from 'store/redux-store/margin-tool/helper';
import {
  getSplitsWithAdditionalPremium,
  getTotalReimbursement,
  roundToTwoDecimals,
  transformSplitsToPeopleSoftValues,
} from '@AMIEWEB/MarginTool/Components/PayPackage/helper';
import { PreviewContainerFieldNames } from '@AMIEWEB/MarginTool/Components/PayPackage/enum';
import moment from 'moment';
import { getExtensionDuration } from 'app/helpers/getExtensionDuration';
import { getRatesWeeklyCalculatedValue } from '@AMIEWEB/MarginTool/Components/PayPackage/PayPackagePreview/helper';
import {
  IPlacementMarginCalculatedScenarioUpdateRequest,
  IPlacementMarginCalculatedScenarioValue,
} from './placement-assignment.model';

const TrackPeopleSoftCallFlagVariation = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
const TrackUpdateScnarioCalculatedValues = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.updateScnarioCalculatedValues);
const TrackInitialMarginToolsDataFlagVariation = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.initialMarginToolsData);

export function* getPlacementMarginTreeViewLookupData() {
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(marginToolService.getPicklistData);
    if (httpSuccess(response.status)) {
      yield put(placementMarginAssignmentActions.setTreeViewDataLookup(response.data));
    } else {
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('common.failedLoad'),
          severity: 'error',
        }),
      );
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'getTreeViewLookupData',
        area: 'src/store/redux-store/margin-tool/slices/assignment/assignment.saga.ts',
      },
    });
  }
}

export function* fetchPlacementMarginPickListData() {
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(marginToolService.getPicklistData);
    if (httpSuccess(response.status)) {
      const filteredInsuranceData = insuranceData(response?.data);
      yield put(
        placementMarginAssignmentActions.setInsuranceStatus(formatInsuranceTypeAheadOptions(filteredInsuranceData)),
      );
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchShiftData',
        area: 'src/store/redux-store/margin-tool/slices/assignment/assignment.saga.ts',
      },
    });
  }
}

export function* getAutoSaveCalculatedScenarioValue() {
  try {
    const headerData = yield select(selectSelectedPlacementMarginScenario);
    const existingRates: IPlacementMarginOverTimeHourlyRateItem[] = yield select(
      selectPlacementMarginOverTimeHourlyRates,
    );
    const bookingData = yield select(selectPlacementMarginBookingPeriod);
    const payPackageDetailsValues = headerData?.assignment?.payPackageDetailsValues;
    const brand = bookingData?.brand;

    const splits = headerData?.splits;
    const tlaEnabled = headerData?.tla;
    const placementMarginCalculatedSplitValue = [];

    let splitsWithAdditionalPremiumPay = getSplitsWithAdditionalPremium(splits, payPackageDetailsValues) || [];
    let scnarioWeeklyGross = splitsWithAdditionalPremiumPay?.reduce((acc, item) => {
      const duration = getExtensionDuration(moment(item?.effectiveStartDate), item?.effectiveEndDate);
      let totalWeeklyGrossItem = 0;
      const hoursPerWeek = item?.hoursPerWeek;
      const hoursPerShift = item?.hoursPerShift;

      const currSplitRates = existingRates?.find(x => x?.splitId === item?.splitId);

      let payRateCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.payRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });
      let overtimeCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.overTimeRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });
      let premiumPayCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.premiumPayRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });
      let doubletimeCalculatedValue = getRatesWeeklyCalculatedValue({
        fieldName: PreviewContainerFieldNames.doubletimeRate,
        currSplitRates,
        eachSplit: item,
        hoursPerWeek: item?.hoursPerWeek,
      });

      if (payRateCalculatedValue) {
        totalWeeklyGrossItem += payRateCalculatedValue;
      }
      if (overtimeCalculatedValue) {
        totalWeeklyGrossItem += overtimeCalculatedValue;
      }
      if (premiumPayCalculatedValue) {
        totalWeeklyGrossItem += premiumPayCalculatedValue;
      }
      if (doubletimeCalculatedValue) {
        totalWeeklyGrossItem += doubletimeCalculatedValue;
      }
      if (tlaEnabled) {
        const tlaShiftsPerWeek = getTlaShiftsPerWeek(hoursPerWeek, hoursPerShift);
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaCarAllowance)) {
          const carAllowance =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaCarAllowance]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += carAllowance;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaMealAllowance)) {
          const mealAllowance =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaMealAllowance]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += mealAllowance;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaCellPhoneStipend)) {
          const cellPhoneStipend =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaCellPhoneStipend]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += cellPhoneStipend;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.tlaShiftCompletionBonus)) {
          const shiftCompletionBonus =
            roundToTwoDecimals(item[PreviewContainerFieldNames.tlaShiftCompletionBonus]) * tlaShiftsPerWeek;
          totalWeeklyGrossItem += shiftCompletionBonus;
        }
      } else {
        if (item?.hasOwnProperty(PreviewContainerFieldNames.mealPerDiem)) {
          totalWeeklyGrossItem += roundToTwoDecimals(item[PreviewContainerFieldNames.mealPerDiem]) * 7;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.lodgingPerDiem)) {
          totalWeeklyGrossItem += roundToTwoDecimals(item[PreviewContainerFieldNames.lodgingPerDiem]) * 7;
        }
        if (item?.hasOwnProperty(PreviewContainerFieldNames.cellPhoneStipend)) {
          totalWeeklyGrossItem += roundToTwoDecimals(item[PreviewContainerFieldNames.cellPhoneStipend]) * 7;
        }
      }
      placementMarginCalculatedSplitValue.push({
        splitId: item?.splitId,
        weeklyGrossPay: totalWeeklyGrossItem?.toFixed(2),
      });
      const weeklyTotal = totalWeeklyGrossItem * duration;
      return acc + weeklyTotal;
    }, 0);
    const totalReimbursement = getTotalReimbursement(headerData, brand);
    const placementMarginCalculatedScenarioValue: IPlacementMarginCalculatedScenarioValue = {
      weeklyGrossPay: (scnarioWeeklyGross + totalReimbursement)?.toFixed(2),
      splits: placementMarginCalculatedSplitValue,
    };
    return {
      placementMarginCalculatedScenarioValue,
    };
  } catch (error) {
    console.error('Failed to get autosave scenario context:', error);
  }
}

export function* getPlacementMarginPeopleSoftCalculation(
  action: PayloadAction<{
    formData: IAssignmentForm;
    isDataUpdateFlow?: boolean | false | null;
    formDataRaw?: IMarginToolSave | null | undefined;
  }>,
) {
  const marginCancellationSource = axios.CancelToken.source();
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
    const assignmentForm = action.payload.formData;
    const currentUser = yield select(selectUser);
    const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
    const marginDetailsData = yield select(selectPlacementMarginToolDetailsData);
    const currentMarginDetail = findMarginDetailByBookingPeriodId(marginDetailsData, selectedScenario?.bookingPeriodId);
    const editTimeOffs = yield select(selectPlacementMarginEditTimeOffs);
    const existingRates: IPlacementMarginOverTimeHourlyRateItem[] = yield select(
      selectPlacementMarginOverTimeHourlyRates,
    );

    const autosave = action.payload.isDataUpdateFlow ?? false;
    const timeOffs = getFormattedTimeOff(editTimeOffs);
    assignmentForm.timeOffRequest = timeOffs ?? [];
    const requestPayload: IPeopleSoftMarginDetailsRequest = createRequestPayloadForPeopleSoftCalculation(
      assignmentForm,
      selectedScenario,
      currentUser,
      currentMarginDetail,
      selectedScenario?.timestamp,
      autosave,
      existingRates,
    );
    const isCancelTriggered = yield select(selectPlacementMarginCancelTriggeredStatus);
    if (isCancelTriggered) {
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress);
      yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
      return;
    }

    if (action.payload.isDataUpdateFlow) {
      manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress);
    }
    const response = yield call(
      TrackPeopleSoftCallFlagVariation,
      marginToolService.getPeopleSoftCalculatedValues,
      requestPayload,
      marginCancellationSource.token,
    );
    const scenarioResponse: IMarginUpdateResponse = response?.data;
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.View;
    }
    if (isCancelTriggered) {
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
      manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress);
      yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
      return;
    }
    if (httpSuccess(response.status)) {
      if (!scenarioResponse?.inError) {
        const data = scenarioResponse?.payload;
        const scenarioCalculationResponse: IPeopleSoftCalculationResult = data;
        const updatedScenario = {
          ...selectedScenario,
          grossProfit: scenarioCalculationResponse.grossProfit,
          grossMargin: scenarioCalculationResponse.grossMargin,
          revenue: scenarioCalculationResponse.revenue,
          negotiatedContributionMargin: scenarioCalculationResponse.negotiatedContributionMargin,
          timestamp: scenarioCalculationResponse.timestamp ?? selectedScenario.timestamp,
        };
        // Dispatch the updated scenario
        yield put(placementMarginScenarioActions.setPlacementMarginScenario(updatedScenario));
        const peopleSoftValuesArray = transformSplitsToPeopleSoftValues(scenarioCalculationResponse.marginCostCalculationWeeklyRates);
        yield put(placementMarginScenarioActions.setPeopleSoftValues(peopleSoftValuesArray));

        const existingRatesTemp: IPlacementMarginOverTimeHourlyRateItem[] = yield select(
          selectPlacementMarginOverTimeHourlyRates,
        );
        const updatedRates = updateMarginOverTimeHourlyRatesFromCalculation(
          existingRatesTemp,
          scenarioCalculationResponse,
        );
        yield put(placementMarginScenarioActions.setPlacementMarginOverTimeHourlyRates(updatedRates));

        const updatedMarginDetailsData = updateMarginDetailsResponseByScenario(marginDetailsData, updatedScenario);
        yield put(placementMarginScenarioActions.setPlacementMarginDetailResponse(updatedMarginDetailsData));

        if (isCancelTriggered) {
          manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
          manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress);
          yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
          return;
        }
        const isSaveTriggered = yield select(selectPlacementMarginSaveTriggeredStatus);
        if (isSaveTriggered) {
          if (action.payload.formDataRaw) {
            const body = action.payload.formDataRaw;
            yield put(
              placementMarginScenarioActions.postPlacementMarginToolDetails({
                formData: body,
                loadDetails: true,
              }),
            );
          }
        } else if (autosave && selectedScenario?.scenarioId > 0) {
          const autoSaveScenarioContext = yield call(getAutoSaveCalculatedScenarioValue);
          const placementMarginCalculatedScenarioUpdateRequest: IPlacementMarginCalculatedScenarioUpdateRequest = {
            scenarioId: selectedScenario?.scenarioId,
            userId: currentUser?.userInfo?.employeeId,
            weeklyGrossPay: autoSaveScenarioContext.placementMarginCalculatedScenarioValue.weeklyGrossPay,
            splits: autoSaveScenarioContext.placementMarginCalculatedScenarioValue.splits,
          };
          const responseUpdateCalculated = yield call(
            TrackUpdateScnarioCalculatedValues,
            marginToolService.UpdatePlacementMarginScenarioCalculatedValuesCommand,
            placementMarginCalculatedScenarioUpdateRequest,
            marginCancellationSource.token,
          );
          if (httpSuccess(responseUpdateCalculated.status) && responseUpdateCalculated.data) {
            const updatedTimeStamp = responseUpdateCalculated.data;
            const selectedScenarioLc = yield select(selectSelectedPlacementMarginScenario);
            if (selectedScenarioLc) {
              const calculatedSplits =
                autoSaveScenarioContext?.placementMarginCalculatedScenarioValue?.splits ?? [];
              const updatedSplits = (selectedScenarioLc?.splits ?? []).map(splitItem => {
                const calculatedSplit = calculatedSplits?.find(x => x?.splitId === splitItem?.splitId);
                if (!calculatedSplit) {
                  return splitItem;
                }
                return {
                  ...splitItem,
                  weeklyGrossPay: calculatedSplit.weeklyGrossPay,
                };
              });
              const updatedScenario = {
                ...selectedScenarioLc,
                weeklyGrossPay: autoSaveScenarioContext.placementMarginCalculatedScenarioValue.weeklyGrossPay,
                splits: updatedSplits,
                timestamp: updatedTimeStamp ?? selectedScenarioLc.timestamp,
              };
              yield put(placementMarginScenarioActions.setPlacementMarginScenario(updatedScenario));

              const updatedMarginDetailsData = updateMarginDetailsResponseByScenario(marginDetailsData, updatedScenario);
              yield put(placementMarginScenarioActions.setPlacementMarginDetailResponse(updatedMarginDetailsData));
            }
          }
        }
      } else {
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
              action: IBannerAction.refreshPage,
              justifyActionContent: 'flex-end',
            }),
          );
        } else {
          const data = scenarioResponse?.payload;
          const scenarioCalculationResponse: IPeopleSoftCalculationResult = data;
          const updatedScenario = {
            ...selectedScenario,
            grossProfit: scenarioCalculationResponse.grossProfit,
            grossMargin: scenarioCalculationResponse.grossMargin,
            revenue: scenarioCalculationResponse.revenue,
            negotiatedContributionMargin: scenarioCalculationResponse.negotiatedContributionMargin,
            timestamp: scenarioCalculationResponse.timestamp ?? selectedScenario.timestamp,
          };
          // Dispatch the updated scenario
          yield put(placementMarginScenarioActions.setPlacementMarginScenario(updatedScenario));
          const peopleSoftValuesArray = transformSplitsToPeopleSoftValues(scenarioCalculationResponse.marginCostCalculationWeeklyRates);
          yield put(placementMarginScenarioActions.setPeopleSoftValues(peopleSoftValuesArray));
          
          const updatedMarginDetailsData = updateMarginDetailsResponseByScenario(marginDetailsData, updatedScenario);
          yield put(placementMarginScenarioActions.setPlacementMarginDetailResponse(updatedMarginDetailsData));
          yield put(
            globalActions.setSnackBar({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
            }),
          );
        }
      }
    }
    yield put(placementMarginScenarioActions.setSavePlacementMarginTriggeredStatus(false));
    yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress);
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );
    yield put(placementMarginScenarioActions.setSavePlacementMarginTriggeredStatus(false));
    yield put(placementMarginScenarioActions.setCancelPlacementMarginTriggeredStatus(false));
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginTool);
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.peopleSoftCallMarginToolProgress);
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'getPeopleSoftCalculation',
        area: 'src/store/redux-store/margin-tool/slices/assignment/assignment.saga.ts',
      },
    });
  } finally {
    if (yield cancelled()) {
      marginCancellationSource.cancel();
    }
  }
}

export function* fetchInitialPlacementMarginToolsData() {
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const selectedScenario = yield select(selectSelectedPlacementMarginScenario);
    manuallyIncrementPromiseCounter(PromiseTrackerKeys.placementMarginTool.initialMarginToolsData);
    const response = yield call(
      TrackInitialMarginToolsDataFlagVariation,
      marginToolService.getNewScenarioDefaultValues,
      selectedScenario.placementId,
      selectedScenario.scenarioId,
    );
    if (httpSuccess(response.status)) {
      const scenario = response.data;
      yield put(placementMarginAssignmentActions.setInitialMarginDetailResponse(scenario));
      const marginOverTimeHourlyRates = getMarginOverTimeHourlyRatesFromScenario(scenario);
      yield put(placementMarginScenarioActions.setPlacementMarginOverTimeHourlyRates(marginOverTimeHourlyRates));
    }
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.initialMarginToolsData);
  } catch (error) {
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.initialMarginToolsData);
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'fetchInitialMarginToolsData',
        area: 'src/store/redux-store/margin-tool/slices/assignment/assignment.saga.ts',
      },
    });
  }
}

export function* placementMarginAssignmentSaga() {
  yield takeLatest(
    placementMarginAssignmentActions.getPlacementMarginTreeViewDataForPickList,
    getPlacementMarginTreeViewLookupData,
  );
  yield takeLatest(
    placementMarginAssignmentActions.getPlacementMarginPickListDetails,
    fetchPlacementMarginPickListData,
  );
  yield takeLatest(
    placementMarginAssignmentActions.triggerPlacementMarginPeopleSoftCalculation,
    getPlacementMarginPeopleSoftCalculation,
  );
  yield takeLatest(
    placementMarginAssignmentActions.getInitialPlacementMarginToolsData,
    fetchInitialPlacementMarginToolsData,
  );
}
